/**
 * The Equipes screen's level bar (Figma `315:11169`): the campaign's levels as an ordered path,
 * chevrons between the chips reading it as a hierarchy rather than a set of filters
 * (`315-11169-09`), plus the link into the campaign's unassigned accounts (`315-11169-04`).
 *
 * `unassigned=true` names a filter no route reads yet -- `web`'s tracked-accounts screen has no
 * team filter today, only the API does -- so the link is real but its destination does not act on
 * the query string until that screen grows one.
 */
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { Badge } from '../../../ui/badge';
import { ACCOUNTS_PATH } from '../../control/paths';
import { plural, t } from '../i18n';
import type { LevelOut } from './schemas';

export function LevelSummary(props: {
  campaignId: string;
  levels: LevelOut[];
  unassignedAccounts: number;
}): ReactNode {
  const { campaignId, levels, unassignedAccounts } = props;
  const locale = getLocale();
  const sorted = [...levels].sort((a, b) => a.position - b.position);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <ol className="flex flex-wrap items-center gap-2" aria-label={t('teams.list.levels.label')}>
        {sorted.map((level, index) => (
          <li key={level.id} className="flex items-center gap-2">
            {index > 0 && <ChevronRight aria-hidden className="size-3.5 text-muted-foreground" />}
            <Badge variant="outline">
              {level.name} {integer(level.teams, locale)}
            </Badge>
          </li>
        ))}
      </ol>
      {unassignedAccounts > 0 && (
        <Link
          to={ACCOUNTS_PATH}
          params={{ campaignId }}
          search={{ unassigned: true }}
          className="flex items-center gap-1 text-body-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
        >
          <ArrowUpRight aria-hidden className="size-3.5" />
          {plural('teams.unassigned.link.other', unassignedAccounts, {
            count: integer(unassignedAccounts, locale),
          })}
        </Link>
      )}
    </div>
  );
}
